import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { apiLaminas } from '@/api/laminas';
import { SearchableSelect } from '@/components/common/SearchableSelect';

// Navegar el JUEGO de planos (F-3). Sin esto el visor es un callejón sin
// salida: para pasar de A-100 a A-101 había que volver a la grilla, buscar la
// miniatura y entrar de nuevo — con el zoom perdido en el camino. En un juego
// de 80 láminas eso es inviable, y es exactamente lo que Procore resuelve.
//
// El juego es UNA lista ordenada por carpeta y después por nombre, igual que la
// grilla. Las flechas recorren esa lista ENTERA, no solo la carpeta actual: un
// juego se revisa de punta a punta y cortar el recorrido en cada carpeta
// obligaría a volver al combo justo en el salto más frecuente.

interface Props {
  idActual: number;
  onAbrir: (id: number) => void;
}

export function NavegadorLaminas({ idActual, onAbrir }: Props) {
  // MISMA `queryKey` que la grilla a propósito: TanStack sirve del caché y esto
  // no agrega ni una request. Pedirla acá es gratis y evita tener que subir el
  // estado de la lista a la página.
  const { data } = useQuery({
    queryKey: ['laminas'],
    queryFn: () => apiLaminas.lista(),
  });

  const juego = useMemo(() => {
    // Sin carpeta van al final, igual que en la grilla: el orden de la barra y
    // el de las miniaturas tienen que ser el mismo o las flechas no se
    // corresponden con lo que la persona vio.
    const clave = (c: string | null) => (c ? `0${c}` : '1');
    return [...(data || [])].sort((a, b) =>
      clave(a.carpeta).localeCompare(clave(b.carpeta)) || a.nombre.localeCompare(b.nombre, 'es'));
  }, [data]);

  const i = juego.findIndex(p => p.id === idActual);
  const anterior = i > 0 ? juego[i - 1] : null;
  const siguiente = i >= 0 && i < juego.length - 1 ? juego[i + 1] : null;

  // Las flechas del teclado son la mitad del valor de esto: revisar un juego es
  // pasar láminas, no apuntarle a un botón. Se ignoran mientras se escribe en un
  // campo, porque ahí la flecha mueve el cursor.
  useEffect(() => {
    const alTeclear = (ev: KeyboardEvent) => {
      if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
      const foco = document.activeElement;
      if (foco instanceof HTMLInputElement || foco instanceof HTMLTextAreaElement) return;
      const destino = ev.key === 'ArrowLeft' ? anterior : siguiente;
      if (destino) { ev.preventDefault(); onAbrir(destino.id); }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [anterior, siguiente, onAbrir]);

  // Una sola lámina no es un juego: la barra entera sobra.
  if (juego.length < 2) return null;

  return (
    <div className="flex min-w-0 items-center gap-1">
      <Flecha
        hacia="anterior"
        destino={anterior}
        onAbrir={onAbrir}
      />

      <div className="w-56 shrink-0">
        <SearchableSelect
          options={juego.map(p => ({
            value: p.id,
            label: p.nombre,
            sublabel: p.carpeta || 'Sin carpeta',
          }))}
          value={idActual}
          onChange={v => v !== null && onAbrir(v)}
          clearable={false}
          placeholder="Ir a una lámina…"
        />
      </div>

      <Flecha
        hacia="siguiente"
        destino={siguiente}
        onAbrir={onAbrir}
      />

      {/* La posición en el juego: dice cuánto falta, que es lo que uno quiere
          saber revisando un paquete de planos. */}
      <span className="ml-1 shrink-0 font-mono text-[11px] text-brand-gray-400">
        {i + 1}/{juego.length}
      </span>
    </div>
  );
}

function Flecha({ hacia, destino, onAbrir }: {
  hacia: 'anterior' | 'siguiente';
  destino: { id: number; nombre: string } | null;
  onAbrir: (id: number) => void;
}) {
  const Icono = hacia === 'anterior' ? CaretLeft : CaretRight;
  const tecla = hacia === 'anterior' ? '←' : '→';
  return (
    <button
      type="button"
      // El `title` nombra la lámina a la que se va, no la dirección: en un juego
      // largo saber que lo siguiente es "A-201 Cortes" vale más que una flecha.
      title={destino ? `${destino.nombre}  (${tecla})` : `No hay lámina ${hacia}`}
      aria-label={`Lámina ${hacia}`}
      disabled={!destino}
      onClick={() => destino && onAbrir(destino.id)}
      className="rounded p-1.5 text-brand-gray-500 transition-colors hover:bg-brand-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
    >
      <Icono size={16} weight="bold" />
    </button>
  );
}
